import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { RiPencilFill } from 'react-icons/ri';
import { priceFormatter } from 'lib/utils/functions';
import { InventoryItem } from 'lib/context';
import { Button } from 'react-covideo-common';
import { MdDeleteForever } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useCDK } from 'lib/hooks/useCdk';
import { getSecondaryColor } from 'lib/context/theme/utils';

const Wrapper = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.white[100]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[20]};
  border-radius: 5px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const TitleWrapper = styled.div`
  display: block;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => getSecondaryColor(theme, 100)};
`;

const Price = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => getSecondaryColor(theme, 100)};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VehicleInfoGrid = styled.div`
  display: grid;
  row-gap: 24px;
  column-gap: 8px;
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;

const GridItem = styled.div`
  display: block;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutral[80]};
`;

const Value = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutral[80]};
`;

const getVehicleProperties = (t: TFunction) => [
  { value: 'vin', label: t('VIN') },
  { value: 'stockNumber', label: t('Stock Number') },
  { value: 'itemCondition', label: t('Condition') },
  { value: 'engine', label: t('Engine') },
  { value: 'make', label: t('Make') },
  { value: 'model', label: t('Model') },
  { value: 'year', label: t('Year') },
  { value: 'mileage', label: t('Mileage') },
  { value: 'trim', label: t('Trim') },
  { value: 'exteriorColor', label: t('Ext. Color') },
  { value: 'interiorColor', label: t('Int. Color') },
  { value: 'transmission', label: t('Transmission') },
];

type Props = {
  vehicle: InventoryItem;
  onChangeVehicle: (vehicle: InventoryItem) => void;
};

type RenderableKeys = Omit<
  InventoryItem,
  'createdAt' | 'media' | 'videoRequests'
>;

export const VehiclePropertiesGrid = ({ vehicle, onChangeVehicle }: Props) => {
  const { t } = useTranslation();
  const { destructiveToText } = useCDK();
  const { colors } = useTheme();
  const vehicleProperties = getVehicleProperties(t);
  const priceAvailable =
    !!vehicle.price &&
    !!parseInt(vehicle.price) &&
    !isNaN(parseInt(vehicle.price));
  return (
    <Wrapper>
      <Header>
        <TitleWrapper>
          <Title>{vehicle.title}</Title>
          {priceAvailable && (
            <Price>{priceFormatter(parseInt(vehicle.price))}</Price>
          )}
          {!priceAvailable && <Price>{t('Please call for price')}</Price>}
        </TitleWrapper>
        <ButtonsWrapper>
          <Button
            variant='secondary'
            onClick={() => onChangeVehicle(vehicle)}
            text={t('Change Vehicle')}
            icon={<RiPencilFill onClick={() => {}} size={20} />}
          />
          <Button
            variant={destructiveToText}
            onClick={() => onChangeVehicle(vehicle)}
            icon={<MdDeleteForever size={20} color={colors.danger[100]} />}
          />
        </ButtonsWrapper>
      </Header>
      <VehicleInfoGrid>
        {vehicleProperties.map((property, i) => (
          <GridItem key={i}>
            <Label>{property.label}</Label>
            <Value>
              {vehicle[property.value as keyof RenderableKeys] ?? ''}
            </Value>
          </GridItem>
        ))}
      </VehicleInfoGrid>
    </Wrapper>
  );
};
